import React from 'react';

/**
 * Error Boundary global - captura erros de runtime (incluindo o clássico
 * "removeChild" causado por Google Translate, Radix Portals, etc) e
 * impede que a aplicação inteira quebre.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorCount: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Erros de removeChild geralmente são causados por extensões do navegador
    // (Google Translate) manipulando o DOM. Logamos mas seguimos em frente.
    const isBenignDomError =
      error && error.name === 'NotFoundError' &&
      String(error.message || '').includes('removeChild');

    console.error('[ErrorBoundary]', error, errorInfo);

    if (isBenignDomError) {
      // Auto-recovery: tenta re-renderizar após pequeno delay
      setTimeout(() => {
        this.setState((s) => ({
          hasError: false,
          error: null,
          errorCount: s.errorCount + 1,
        }));
      }, 100);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.errorCount < 3) {
      // Loading silencioso enquanto auto-recovery acontece
      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center fade-in">
          <div className="flex flex-col items-center gap-3">
            <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#8B7220] animate-pulse">
              <span className="font-bold text-zinc-950 text-sm">C</span>
            </div>
            <div className="text-[11px] text-zinc-500 tracking-widest uppercase">Recarregando interface...</div>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <div className="text-zinc-100 font-medium text-lg mb-2">Algo inesperado aconteceu</div>
            <div className="text-zinc-400 text-sm mb-5">
              A interface encontrou um erro de renderização. Geralmente isso é causado por extensões do navegador
              (ex: Google Translate ativo na página).
            </div>
            <div className="text-xs text-zinc-600 mb-5 font-mono bg-zinc-900/50 p-3 rounded border border-zinc-800">
              {String(this.state.error || '')}
            </div>
            <div className="flex gap-2 justify-center">
              <button
                onClick={this.handleReset}
                className="btn-gold px-4 py-2 rounded-md text-sm font-medium"
              >
                Tentar Novamente
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-md text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900"
              >
                Recarregar Página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
